import { Button, Flex, Modal, Skeleton } from 'antd';
import { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Plan } from 'modules/main/auth/register/models/plans';
import { onChoosePlan, onGetPlans } from 'core/services/plans';
import NotificationService from 'core/services/notification';
import PlanComponent from 'components/plan/plan';
import LoadingComponent from 'components/loadingComponent/loadingComponent';
import Icon from 'utils/icon';
import { loadScript } from 'utils/loadScript';
import OrderStatusComponent from 'modules/main/wallet/components/orderStatus';
import { useNavigate } from 'react-router-dom';

interface PlanModalComponentProps {
	open: boolean;
	currentPlanKey?: string;
	onClose: () => void;
}

const PlanModalComponent: FunctionComponent<PlanModalComponentProps> = ({ open, onClose, currentPlanKey }) => {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const [step, setStep] = useState<'plan' | 'payment'>('plan');
	const [paymentState, setPaymentState] = useState<'payment' | 'checking'>('payment');
	const [merchantId, setMerchantId] = useState<string>('');

	const [plans, setPlans] = useState<Plan[]>([]);
	const [plansLoading, setPlansLoading] = useState(false);
	const [choosePlanLoading, setChoosePlanLoading] = useState(false);
	const [errorOccurred, setErrorOccurred] = useState<boolean>(false);

	useEffect(() => {
		if (open) {
			viewPlansHandler();
		}
	}, [open]);

	const onCloseHandler = () => {
		setStep('plan');
		onClose();
	};

	const viewPlansHandler = async () => {
		try {
			setPlansLoading(true);

			const response = await onGetPlans();
			setPlans(response.plans);

			setPlansLoading(false);
		} catch (error) {
			setPlansLoading(false);
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
		}
	};

	const choosePlanHandler = async (plan: Plan) => {
		try {
			setChoosePlanLoading(true);
			setStep('payment');

			const response = await onChoosePlan(plan.id);

			loadScript(response.payment_url)
				.then(() => {
					(window as any).ProcessCheckoutPayment({
						Checkout_Method: 3,
						Frame_Id: 'payment_frame',
						SecureVerification_Method: 1,
						Call_Back: ResponseCallBack,
					});
				})
				.catch((error) => {
					console.error('Error loading script:', error);
				});

			setChoosePlanLoading(false);
		} catch (error) {
			setChoosePlanLoading(false);
			setErrorOccurred(true);
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.SUBMIT_ERROR'));
		}
	};

	const ResponseCallBack = (response: { merchantReference: string; orderId: string }) => {
		setMerchantId(response.merchantReference);
		setPaymentState('checking');
	};

	return (
		<Modal
			open={open}
			onCancel={onCloseHandler}
			title={null}
			footer={null}
			closeIcon={null}
			width="fit-content"
			centered
			className={step == 'plan' ? 'transparent' : ''}>
			<Flex justify="center" align="center" style={{ minWidth: step === 'plan' ? 350 : 500, minHeight: 535 }}>
				{step === 'plan' ? (
					plansLoading ? (
						<LoadingComponent />
					) : (
						<Flex gap={40}>
							{plans
								?.filter((plan) => plan.key != currentPlanKey)
								.map((plan) => (
									<PlanComponent key={plan.id} plan={plan} bordered={false} centered onChoosePlan={choosePlanHandler} disabled={choosePlanLoading} />
								))}
						</Flex>
					)
				) : choosePlanLoading ? (
					<LoadingComponent />
				) : errorOccurred ? (
					<Flex vertical align="center" gap={20}>
						<Icon name="error" width={150} height={150} />
						<div className="text-center">
							<h5>{t('COMMON.PAYMENT_ERROR_GATEWAY')}</h5>
							<h5>{t('COMMON.TRY_AGAIN')}</h5>
						</div>

						<Button className="mt-5" type="primary" onClick={onCloseHandler}>
							Close
						</Button>
					</Flex>
				) : paymentState == 'payment' ? (
					<iframe style={{ border: 'none', borderRadius: 10, width: '100%', height: 500 }} id="payment_frame"></iframe>
				) : (
					<OrderStatusComponent
						merchantId={merchantId}
						onStatusChange={(status) => {
							if (status === 'success') navigate(0);
						}}
					/>
				)}
			</Flex>
		</Modal>
	);
};

export default PlanModalComponent;
